import React, { ReactNode } from "react";
import { LucideIcon } from "lucide-react";
import { Button } from "components/ui/Button";
import { BaseIconProps } from "components/icons/IconFactory";
import { PenIcon } from "components/icons/Icons";

export type GenericBoxProps = {
  /** Function to handle the action button click. Without it, the box shows no button. */
  onSelect?: () => void;
  /** Icon of the action button; a pen by default (edition). Pass an eye for a read-only view. */
  actionIcon?: React.FC<BaseIconProps>;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Title displayed in uppercase */
  title: string;
  /** Text shown right under the title, in the title column */
  subtitle?: ReactNode;
  /** Optional className for the main container */
  className?: string;

  children?: ReactNode;
};

/**
 * A generic box component that displays an icon, a title, an optional subtitle, content lines,
 * and an edit button
 */
const GenericBox: React.FC<GenericBoxProps> = ({
  onSelect,
  actionIcon: ActionIcon = PenIcon,
  icon: Icon,
  title,
  subtitle,
  className = "",
  children
}) => {
  return (
    <div className={`border flex flex-col gap-3.5 p-4 rounded-xl overflow-hidden h-full ${className}`}>
      <div className={"flex justify-between gap-4"}>
        <div className="flex grow w-full">
          <div className="mr-4">
            <div className="w-7 h-7 min-w-9 min-h-9 bg-secondary rounded-full flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
          </div>
          <div className="flex flex-col w-full min-w-24 gap-1">
            <p className="text-sm font-light">{title}</p>
            {subtitle !== undefined && <p className="text-primary text-sm">{subtitle}</p>}
          </div>
        </div>
        {onSelect && (
          <Button color="slate" className="self-baseline" round
            onClick={() => onSelect()}>
            <ActionIcon className="text-black" />
          </Button>
        )}
      </div>
      {children}
    </div>
  );
};

export default GenericBox;
